/**
 * AiChecker Alpine component
 * Competitor AI Visibility Checker - 分析竞争对手网站的 AI 可读性
 */
import { aiCheckerApi, AiCheckerApi } from '@utils/aiCheckerApi.js';
import { SCAN_STEPS } from '@constants/aiChecker.js';

function createUnknownScanResults() {
  return {
    llmsTxt: { ...AiCheckerApi.getStatusInfo('unknown') },
    schemaOrg: { ...AiCheckerApi.getStatusInfo('unknown') },
    ndjson: { ...AiCheckerApi.getStatusInfo('unknown') },
    robots: { ...AiCheckerApi.getStatusInfo('unknown') },
    sitemap: { ...AiCheckerApi.getStatusInfo('unknown') },
  };
}

function createDefaultScoreDetails() {
  return {
    discovery: { score: 0, level: 'Unknown', color: 'gray', icon: 'visibility' },
    structure: { score: 0, level: 'Unknown', color: 'gray', icon: 'account_tree' },
    machineReadability: { score: 0, level: 'Unknown', color: 'gray', icon: 'smart_toy' },
  };
}

export function aiChecker() {
  return {
    // URL 输入
    competitorUrl: '',
    urlError: '',

    // 扫描状态
    scanState: 'idle', // 'idle' | 'scanning' | 'completed' | 'error'
    scanProgress: 0,
    currentStep: 1, // 1-4 对应侧边栏步骤
    errorMessage: '',
    progressTimerId: null,

    // 扫描结果
    scanResults: createUnknownScanResults(),

    // 评分数据
    aiReadinessScore: 0,
    scoreAnimationProgress: 0,
    lastUpdateTime: '',

    scoreDetails: createDefaultScoreDetails(),

    // 建议
    recommendations: [],

    /**
     * 验证 URL 是否有效
     */
    validateUrl() {
      const urlPattern = /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/;
      const isEmpty = !this.competitorUrl.trim();

      this.urlError = isEmpty
        ? 'Please enter a competitor URL'
        : (urlPattern.test(this.competitorUrl) ? '' : 'Please enter a valid URL (e.g., https://competitor.com)');
    },

    /**
     * URL 是否有效（计算属性）
     */
    get isValidUrl() {
      return this.competitorUrl.trim() && !this.urlError;
    },

    /**
     * 是否正在扫描（计算属性）
     */
    get isScanning() {
      return this.scanState === 'scanning';
    },

    /**
     * 是否已完成（计算属性）
     */
    get isCompleted() {
      return this.scanState === 'completed';
    },

    resetScanOutput() {
      this.scanResults = createUnknownScanResults();
      this.recommendations = [];
      this.aiReadinessScore = 0;
      this.scoreAnimationProgress = 0;
      this.lastUpdateTime = '';
      this.scoreDetails = createDefaultScoreDetails();
    },

    stopProgressSimulation() {
      if (this.progressTimerId) {
        clearInterval(this.progressTimerId);
        this.progressTimerId = null;
      }
    },

    startProgressSimulation() {
      const maxProgressBeforeComplete = 95;
      const checkpoints = SCAN_STEPS
        .map(({ progress, step }) => ({
          progress: Math.min(progress, maxProgressBeforeComplete),
          step,
        }))
        .toSorted((a, b) => a.progress - b.progress);

      this.stopProgressSimulation();

      this.progressTimerId = setInterval(() => {
        if (this.scanState !== 'scanning') {
          this.stopProgressSimulation();
          return;
        }

        if (this.scanProgress >= maxProgressBeforeComplete) {
          return;
        }

        // 快速起步，后期放缓，避免在请求未完成时提前到 100%
        const remaining = maxProgressBeforeComplete - this.scanProgress;
        const increment = Math.max(1, Math.ceil(remaining / 10));
        this.scanProgress = Math.min(maxProgressBeforeComplete, this.scanProgress + increment);

        let currentStep = 2;
        for (const checkpoint of checkpoints) {
          if (this.scanProgress >= checkpoint.progress) {
            currentStep = checkpoint.step;
          } else {
            break;
          }
        }
        this.currentStep = currentStep;
      }, 250);
    },

    /**
     * 开始分析
     */
    async startAnalysis() {
      this.validateUrl();
      if (this.urlError) return;

      this.resetScanOutput();
      this.scanState = 'scanning';
      this.scanProgress = 0;
      this.currentStep = 2;
      this.errorMessage = '';

      await this.performRealScan();
    },

    /**
     * 执行真实扫描
     */
    async performRealScan() {
      try {
        this.startProgressSimulation();

        // 调用 API 执行扫描（与进度动画并行）
        const result = await aiCheckerApi.scan(this.competitorUrl);
        this.stopProgressSimulation();
        this.scanProgress = 100;
        this.currentStep = 4;

        // 映射结果到前端状态
        this.mapApiResultToState(result);

        // 一拿到真实结果就切换到完成态，避免“100%后长时间仍显示扫描中”
        this.scanState = 'completed';

        // 开始评分动画
        await this.animateScore();

        // 格式化时间戳
        this.lastUpdateTime = AiCheckerApi.formatTimestamp(result.timestamp);
      } catch (error) {
        this.stopProgressSimulation();
        this.errorMessage = error.message || '扫描失败，请稍后重试';
        this.scanState = 'error';
        console.error('AI Checker scan failed:', error);
      }
    },

    /**
     * 映射 API 结果到前端状态
     */
    mapApiResultToState(apiResult) {
      const state = AiCheckerApi.mapApiResultToState(apiResult);

      this.scanResults = state.scanResults;
      this.aiReadinessScore = state.aiReadinessScore;
      this.scoreDetails = state.scoreDetails;
      this.recommendations = state.recommendations;
    },

    /**
     * 获取状态显示信息
     */
    getStatusInfo(status) {
      const statusMap = {
        missing: {
          status: 'missing',
          icon: 'close',
          label: 'Missing',
          colorClass: 'text-red-500',
          bgClass: 'bg-red-50',
          borderClass: 'border-red-100'
        },
        detected: {
          status: 'detected',
          icon: 'check_circle',
          label: 'Detected',
          colorClass: 'text-green-600',
          bgClass: 'bg-green-50',
          borderClass: 'border-green-100'
        },
        incomplete: {
          status: 'incomplete',
          icon: 'warning',
          label: 'Incomplete',
          colorClass: 'text-orange-600',
          bgClass: 'bg-orange-50',
          borderClass: 'border-orange-100'
        },
        unknown: {
          status: 'unknown',
          icon: 'help_outline',
          label: 'Unknown',
          colorClass: 'text-gray-500',
          bgClass: 'bg-gray-50',
          borderClass: 'border-gray-100'
        }
      };

      return statusMap[status] || statusMap.unknown;
    },

    /**
     * 评分圆环动画
     */
    async animateScore() {
      this.scoreAnimationProgress = 0;
      const duration = 1500; // 1.5 秒
      const steps = 60;
      const increment = this.aiReadinessScore / steps;

      for (let i = 0; i <= steps; i++) {
        await new Promise(resolve => setTimeout(resolve, duration / steps));
        this.scoreAnimationProgress = Math.min(increment * i, this.aiReadinessScore);
      }

      this.scoreAnimationProgress = this.aiReadinessScore;
    },

    /**
     * 获取步骤状态
     */
    getStepStatus(stepNumber) {
      if (this.currentStep > stepNumber) return 'completed';
      if (this.currentStep === stepNumber) return 'current';
      return 'pending';
    },

    /**
     * 获取步骤样式类
     */
    getStepClasses(stepNumber) {
      const status = this.getStepStatus(stepNumber);

      const baseClasses = 'group relative flex items-center gap-4 px-5 py-3 border-l-4 transition-all';

      const statusClasses = {
        completed: 'border-success bg-blue-50',
        current: 'border-primary bg-blue-50',
        pending: 'border-transparent hover:border-primary hover:bg-blue-50'
      };

      return `${baseClasses} ${statusClasses[status]}`;
    },

    /**
     * 获取步骤数字样式
     */
    getStepNumberClasses(stepNumber) {
      const status = this.getStepStatus(stepNumber);

      const baseClasses = 'relative z-10 flex size-7 shrink-0 items-center justify-center rounded-full font-bold text-xs ring-4 ring-white shadow-sm';

      const statusClasses = {
        completed: 'bg-success text-white shadow-emerald-200',
        current: 'bg-primary text-white shadow-blue-200',
        pending: 'bg-white border border-gray-200 text-gray-500'
      };

      return `${baseClasses} ${statusClasses[status]}`;
    }
  };
}
